import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const variantStyles = {
  primary:
    'bg-accent text-white hover:bg-accent-hover active:bg-accent-hover shadow-sm',
  secondary:
    'bg-surface border border-border text-text hover:bg-surface-subtle active:bg-surface-subtle',
  outline:
    'border border-border bg-transparent text-muted hover:bg-surface-subtle hover:text-text active:bg-surface-subtle',
  ghost:
    'text-muted hover:bg-surface-subtle hover:text-text active:bg-surface-subtle',
  subtle:
    'bg-surface-subtle text-muted hover:bg-border/60 hover:text-text active:bg-border/80',
  danger:
    'bg-danger text-white hover:bg-danger/90 active:bg-danger/80 shadow-sm',
} as const;

// Une icône sans taille explicite prend celle du bouton : 14 px en xs/sm, 16 px
// au-delà. Une icône qui porte déjà sa classe `h-*` n'est pas touchée.
const sizeStyles = {
  xs: 'h-7 px-2 text-xs gap-1 rounded [&>svg:not([class*="_h-"])]:h-3.5 [&>svg:not([class*="_h-"])]:w-3.5',
  sm: 'h-8 px-3 text-xs gap-1.5 rounded [&>svg:not([class*="_h-"])]:h-3.5 [&>svg:not([class*="_h-"])]:w-3.5',
  md: 'h-9 px-4 text-sm gap-2 rounded [&>svg:not([class*="_h-"])]:h-4 [&>svg:not([class*="_h-"])]:w-4',
  lg: 'h-11 px-6 text-sm gap-2 rounded [&>svg:not([class*="_h-"])]:h-4 [&>svg:not([class*="_h-"])]:w-4',
  // Bouton icône seule : carré. Fournir `aria-label` (obligatoire à l'usage).
  'icon-sm': 'h-7 w-7 p-0 rounded [&>svg:not([class*="_h-"])]:h-3.5 [&>svg:not([class*="_h-"])]:w-3.5',
  icon: 'h-8 w-8 p-0 rounded [&>svg:not([class*="_h-"])]:h-4 [&>svg:not([class*="_h-"])]:w-4',
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variantStyles;
  size?: keyof typeof sizeStyles;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading = false, disabled, children, type = 'button', ...props }, ref) => {
    return (
      <button
        ref={ref}
        // `button` par défaut : un <button> sans type soumet le formulaire qui le
        // contient (« Ajouter un critère » dans une modale validait la clause).
        // Le bouton de soumission déclare `type="submit"` explicitement.
        type={type}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
          'disabled:pointer-events-none disabled:opacity-40',
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
